import { useMemo } from "react";
import type { useMailSnooze } from "@/hooks/useMailSnooze";
import { isSnoozedNavFolder } from "@/lib/snooze/constants";
import { isSnoozedListFolder } from "@/lib/snooze/snoozeNavFolder";
import type { MailAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import type { MailFolder } from "@/lib/types/mail";
import { useInboxAttachmentFlags } from "@/hooks/useInboxAttachmentFlags";
import type { useMessageLabels } from "@/hooks/useMessageLabels";
import type { PendingDraftSave } from "@/lib/compose/draftSavePending";
import {
  applyInboxListFilter,
  splitInboxListFilter,
  type InboxListFilter,
} from "@/lib/mail/messageLabels";
import type { MailMessageSummary } from "@/lib/types/mail";

type Options = {
  isOutboxView: boolean;
  isDraftsFolder: boolean;
  accountId: number | undefined;
  selectedFolder: string;
  listFilter: InboxListFilter;
  inboxMessages: MailMessageSummary[];
  messageLabels: ReturnType<typeof useMessageLabels>;
  outboxMessages: MailMessageSummary[];
  pendingDraftSaves?: PendingDraftSave[];
  mailSnooze?: ReturnType<typeof useMailSnooze>;
  folders?: MailFolder[];
  accountSettings?: Pick<MailAccountSettings, "folderSlotOverrides">;
};
function pendingDraftToSummary(entry: PendingDraftSave): MailMessageSummary {
  const uid = entry.uid ?? 0;
  return {
    uid,
    subject: entry.subject.trim() || "(Sans objet)",
    from: "",
    to: entry.to.trim() || undefined,
    date: new Date().toISOString(),
    seen: true,
    flagged: false,
    size: 0,
    hasAttachments: entry.hasAttachments === true,
  };
}

function mergePendingDraftRows(
  messages: MailMessageSummary[],
  pendingDraftSaves: PendingDraftSave[]
): MailMessageSummary[] {
  if (pendingDraftSaves.length === 0) return messages;

  const deletingUids = new Set(
    pendingDraftSaves
      .filter((entry) => entry.deleting && entry.uid != null && entry.uid > 0)
      .map((entry) => entry.uid as number)
  );

  let merged = messages.map((message) =>
    deletingUids.has(message.uid) ? { ...message, draftDeleting: true } : message
  );

  const knownUids = new Set(merged.map((m) => m.uid).filter((uid) => uid > 0));
  const pendingRows = pendingDraftSaves
    .filter((entry) => !entry.deleting)
    .filter((entry) => entry.uid == null || entry.uid <= 0 || !knownUids.has(entry.uid))
    .map(pendingDraftToSummary);

  if (pendingRows.length === 0) return merged;
  return [...pendingRows, ...merged];
}

/**
 * Pipeline d’affichage liste : PJ → labels → filtres client.
 * « Non lus » est géré séparément via IMAP (voir splitInboxListFilter).
 */
export function useInboxListDisplay({
  isOutboxView,
  isDraftsFolder,
  accountId,
  selectedFolder,
  listFilter,
  inboxMessages,
  messageLabels,
  outboxMessages,
  pendingDraftSaves = [],
  mailSnooze,
  folders = [],
  accountSettings,
}: Options) {
  const isSnoozedVirtualView = isSnoozedNavFolder(selectedFolder);
  const isSnoozedImapView =
    !isSnoozedVirtualView &&
    isSnoozedListFolder(selectedFolder, folders, accountSettings) &&
    folders.length > 0;
  const isSnoozedView = isSnoozedVirtualView || isSnoozedImapView;
  const { imapUnreadOnly, clientFilter } = useMemo(
    () => splitInboxListFilter(listFilter),
    [listFilter]
  );

  const inboxWithPendingDrafts = useMemo(() => {
    if (isOutboxView || !isDraftsFolder || pendingDraftSaves.length === 0) {
      return inboxMessages;
    }
    return mergePendingDraftRows(inboxMessages, pendingDraftSaves);
  }, [inboxMessages, isDraftsFolder, isOutboxView, pendingDraftSaves]);

  const withAttachmentFlags = useInboxAttachmentFlags(
    isOutboxView ? undefined : accountId,
    isOutboxView ? "INBOX" : selectedFolder,
    inboxWithPendingDrafts
  );

  const labeledMessages = useMemo(
    () => (isOutboxView ? withAttachmentFlags : messageLabels.applyToMessages(withAttachmentFlags)),
    [isOutboxView, messageLabels, withAttachmentFlags]
  );

  const filteredMessages = useMemo(() => {
    if (isOutboxView) return labeledMessages;
    if (isSnoozedView) {
      return mailSnooze?.snoozedMessages ?? [];
    }
    const withoutSnoozed =
      mailSnooze == null
        ? labeledMessages
        : labeledMessages.filter((m) => !mailSnooze.isSnoozed(selectedFolder, m.uid));
    return applyInboxListFilter(withoutSnoozed, clientFilter);
  }, [
    clientFilter,
    isOutboxView,
    isSnoozedView,
    labeledMessages,
    mailSnooze,
    selectedFolder,
  ]);

  const displayMessages = isOutboxView ? outboxMessages : filteredMessages;

  return {
    imapUnreadOnly,
    clientFilter,
    labeledMessages,
    filteredMessages,
    displayMessages,
    isSnoozedView,
    isSnoozedVirtualView,
  };
}
